import styles from './PaymentModalWindow.module.scss';
import Box from '@components/Box';
import ButtonWithTimer, { ButtonWithTimerType } from '@components/ButtonWithTimer/ButtonWithTimer';
import cn from 'classnames';
import FormErrorMessage from '@components/FormErrorMessage';
import moment from 'moment/moment';
import PlacingOrderHeader from '@components/PlacingOrderHeader';
import { Button } from '@components/Button';
import { Column, Row } from '@components/Grid';
import { formatSumWithCurrency } from '@utils/formatValues';
import { IPaymentModalWindowProps } from './types';
import { Loader } from '@components/Loader';
import { Text, TextColor, TextSize, TextWeight } from '@components/Text';
import { useProfile } from '@contexts/ProfileContext';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const PaymentModalWindow: React.FC<IPaymentModalWindowProps> = (props: IPaymentModalWindowProps): React.ReactElement => {
  const {
    dataSendTopUpBalance,
    errorGetPaymentLink,
    handlerOnClickPaymentLink,
    isErrorGetPaymentLink,
    isLoadingGetPaymentLink,
    isLoadingSendTopUpBalance,
    mutateSendTopUpBalance,
    paymentError,
    paymentInProcessed,
    selectedSum,
    subTitle
  } = props;

  const [timer, setTimer] = useState<number>(600);

  const { account } = useProfile();
  const { t } = useTranslation();

  const variableCurrency: string = account?.region === 'TR' ? '$' : '¥';

  const handlerStartTimer = () => {
    let counter = timer;
    const interval = setInterval(() => {
      counter--;
      setTimer(counter);
      if (counter === 0) clearInterval(interval);
    }, 1000);
  };

  const amount = useMemo(() => {
    return Math.round((selectedSum - account!.balance!) * 100) / 100;
  }, [selectedSum, account!.balance])

  return (
      <div className={styles.paymentReturn}>
        <PlacingOrderHeader
            description={subTitle}
            title={`${t('paymentModalWindow.title')} ${formatSumWithCurrency(amount, variableCurrency)}`}
        />
        <Row>
          <Column phone={12}>
            <Box>
              {account!.region === 'TR' ? (
                  <Text
                      className={!paymentInProcessed ? styles.hidden : ''}
                      color={TextColor.PRIMARY}
                      size={TextSize.S}
                      weight={TextWeight.MEDIUM}
                  >
                    {t('paymentModalWindow.expectation') + '... '}
                    <span className={styles.timer}>
                      {moment(timer * 1000).format('mm:ss')}
                    </span>
                  </Text>
              ) : (
                  <div className={styles.qrCode}>
                    {!isLoadingSendTopUpBalance && (
                        <img
                            className={cn(styles.qrCodeImg, paymentInProcessed && styles.qrCodeImgHidden)}
                            alt="qrCode"
                            src={`data:image/svg+xml;base64,${dataSendTopUpBalance?.qrCode}`}
                        />
                    )}
                    {(paymentInProcessed || isLoadingSendTopUpBalance) && (
                        <Loader
                            alignment="center"
                            size="xl"
                        />
                    )}
                  </div>
              )}
            </Box>
          </Column>
        </Row>
        {account!.region === 'TR' ? (
            <Row>
              <Box pt={8}>
                <Button
                    onClick={() => {
                      handlerOnClickPaymentLink((amount).toString());
                      handlerStartTimer();
                    }}
                    pending={isLoadingGetPaymentLink}
                    title={t('paymentModalWindow.paymentLink')}
                />
              </Box>
            </Row>
        ) : (
            <>
              {!paymentInProcessed && (
                  <Row>
                    <Box pt={8}>
                      <ButtonWithTimer
                          onClick={() => mutateSendTopUpBalance({ amount })}
                          label={t('paymentModalWindow.buttonUpdate')}
                          startTimerFromOutside={true}
                          timeout={timer}
                          type={ButtonWithTimerType.LINK}
                      />
                    </Box>
                  </Row>
              )}
            </>
        )}
        {paymentError && <FormErrorMessage error={paymentError}/>}
        {isErrorGetPaymentLink && <FormErrorMessage error={errorGetPaymentLink}/>}
      </div>
  );
};

export default PaymentModalWindow;
